"use client"

import { motion, useScroll, useTransform, useMotionValue, useInView, type HTMLMotionProps, type Variants } from "framer-motion"
import { useEffect, useRef, useState } from "react"

// Reusable animation variants
export const fadeIn: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

export const fadeInLeft: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
}

export const fadeInRight: Variants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0 },
}

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 },
}

export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
}

// Motion wrapper for scroll-triggered animations
export function MotionDiv({
  children,
  variants = fadeIn,
  className,
  ...props
}: HTMLMotionProps<"div"> & { children: React.ReactNode }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      variants={variants}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  )
}

// Section wrapper with staggered children
export function MotionSection({
  children,
  className,
  ...props
}: HTMLMotionProps<"section"> & { children: React.ReactNode }) {
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className={className}
      {...props}
    >
      {children}
    </motion.section>
  )
}

// Counting up number animation — great for stats sections
export function CountUp({
  target,
  duration = 2,
  suffix = "",
  prefix = "",
  className,
}: {
  target: number
  duration?: number
  suffix?: string
  prefix?: string
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isInView) return
    let start = 0
    const end = target
    const range = end - start
    const startTime = performance.now()
    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / (duration * 1000), 1)
      const eased = 1 - Math.pow(1 - progress, 3) // easeOutCubic
      setCount(Math.floor(start + range * eased))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [isInView, target, duration])

  return <span ref={ref} className={className}>{prefix}{count}{suffix}</span>
}

// Parallax wrapper — children move at different scroll speeds
export function Parallax({
  children,
  speed = 0.5,
  className,
}: {
  children: React.ReactNode
  speed?: number
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  })
  const y = useTransform(scrollYProgress, [0, 1], [speed * 100, speed * -100])

  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  )
}

// Hero background with slow zoom — use animate-hero-zoom on the img/div
export function ParallaxHero({
  children,
  imageSrc,
  overlay = "from-black/70 to-black/40",
  className,
}: {
  children: React.ReactNode
  imageSrc?: string
  overlay?: string
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  })
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"])
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0])

  return (
    <section ref={ref} className={`relative min-h-screen overflow-hidden ${className || ""}`}>
      {imageSrc && (
        <motion.div
          style={{ y }}
          className="absolute inset-0 -top-[10%] h-[120%] animate-hero-zoom"
        >
          <img src={imageSrc} alt="" className="w-full h-full object-cover" />
        </motion.div>
      )}
      <div className={`absolute inset-0 bg-gradient-to-br ${overlay}`} />
      <motion.div style={{ opacity }} className="relative z-10 flex items-center min-h-screen">
        {children}
      </motion.div>
    </section>
  )
}

export { motion }